import { api } from "@/lib/api-client";
import type {
  StoreListItem,
  StoreDetail,
  StoreConfig,
  MenuResponse,
  ReviewsResponse,
  AppCategoryItem,
  ListStoresParams,
  GuestListStoresParams,
  MenuParams,
  GuestMenuParams,
  ReviewParams,
  GuestReviewParams,
} from "@/types";

// ── Query string helpers ──

function storeListQs(params?: ListStoresParams): string {
  const qs = new URLSearchParams();
  if (params?.page) qs.set("page", String(params.page));
  if (params?.limit) qs.set("limit", String(params.limit));
  if (params?.search) qs.set("search", params.search);
  if (params?.sortBy) qs.set("sortBy", params.sortBy);
  if (params?.sortOrder) qs.set("sortOrder", params.sortOrder);
  if (params?.template) qs.set("template", params.template);
  if (params?.status) qs.set("status", params.status);
  if (params?.appCategoryUlid) qs.set("appCategoryUlid", params.appCategoryUlid);
  if (params?.tagUlid) qs.set("tagUlid", params.tagUlid);
  if (params?.minRating) qs.set("minRating", String(params.minRating));
  if (params?.maxDeliveryCharge != null) qs.set("maxDeliveryCharge", String(params.maxDeliveryCharge));
  if (params?.hasDiscount) qs.set("hasDiscount", "true");
  if (params?.maxAvgTime != null) qs.set("maxAvgTime", String(params.maxAvgTime));
  if (params?.hasPass) qs.set("hasPass", "true");
  const q = qs.toString();
  return q ? `?${q}` : "";
}

function menuQs(params: MenuParams | GuestMenuParams): string {
  const qs = new URLSearchParams();
  qs.set("store_ulid", params.store_ulid);
  if ("company_ulid" in params) qs.set("company_ulid", params.company_ulid);
  if (params.category_ulid) qs.set("category_ulid", params.category_ulid);
  if (params.product_ulid) qs.set("product_ulid", params.product_ulid);
  if (params.page) qs.set("page", String(params.page));
  if (params.limit) qs.set("limit", String(params.limit));
  if (params.search) qs.set("search", params.search);
  if (params.sortBy) qs.set("sortBy", params.sortBy);
  if (params.sortOrder) qs.set("sortOrder", params.sortOrder);
  if (params.minPrice != null) qs.set("minPrice", String(params.minPrice));
  if (params.maxPrice != null) qs.set("maxPrice", String(params.maxPrice));
  return `?${qs.toString()}`;
}

function reviewQs(params?: ReviewParams | GuestReviewParams): string {
  const qs = new URLSearchParams();
  if (params && "company_ulid" in params) qs.set("company_ulid", params.company_ulid);
  if (params?.page) qs.set("page", String(params.page));
  if (params?.limit) qs.set("limit", String(params.limit));
  if (params?.sortBy) qs.set("sortBy", params.sortBy);
  if (params?.sortOrder) qs.set("sortOrder", params.sortOrder);
  if (params?.filterRating) qs.set("filterRating", String(params.filterRating));
  const q = qs.toString();
  return q ? `?${q}` : "";
}

// ── Authenticated (Customer) ──

export const storeService = {
  list: (params?: ListStoresParams, token?: string) =>
    api.get<StoreListItem[]>(`/stores${storeListQs(params)}`, { token }),

  getById: (ulid: string, token?: string) =>
    api.get<StoreDetail>(`/stores/${ulid}`, { token }),

  getMenu: (params: MenuParams, token?: string) =>
    api.get<MenuResponse>(`/stores/menu/multi-level${menuQs(params)}`, { token }),

  // Force the b2c flow for now — the backend otherwise auto-selects b2c/b2b
  // based on the customer's status, which we don't want to expose yet.
  getConfig: (ulid: string, token?: string) =>
    api.get<StoreConfig>(`/stores/${ulid}/config?view=b2c`, { token }),

  getReviews: (ulid: string, params?: ReviewParams, token?: string) =>
    api.get<ReviewsResponse>(`/stores/${ulid}/reviews${reviewQs(params)}`, { token }),

  getAppCategories: (token?: string) =>
    api.get<AppCategoryItem[]>("/stores/app-categories", { token }),

  /**
   * Vide le cache serveur du flux de découverte pour ce client.
   *
   * À appeler UNIQUEMENT sur un changement de localisation, comme le fait
   * l'app client (`services/api/discovery.ts`). `GET /stores` reflète, lui,
   * la nouvelle position immédiatement — vérifié — mais les carrousels et le
   * feed de découverte sont calculés à l'avance et restaient sur l'ancienne
   * zone, d'où des rayons qui ne correspondaient plus à l'adresse affichée.
   *
   * Sans réponse utile : 204. À traiter en « au mieux », jamais bloquant.
   */
  invalidateDiscovery: (token?: string) =>
    api.post<void>("/stores/discovery/invalidate", undefined, { token }),
};

// ── Guest (unauthenticated, geo-filtered) ──

export const guestStoreService = {
  list: (params: GuestListStoresParams) => {
    const qs = new URLSearchParams({
      latitude: String(params.latitude),
      longitude: String(params.longitude),
      company_ulid: params.company_ulid,
    });
    // Copy all optional params
    if (params.page) qs.set("page", String(params.page));
    if (params.limit) qs.set("limit", String(params.limit));
    if (params.search) qs.set("search", params.search);
    if (params.sortBy) qs.set("sortBy", params.sortBy);
    if (params.sortOrder) qs.set("sortOrder", params.sortOrder);
    if (params.template) qs.set("template", params.template);
    if (params.status) qs.set("status", params.status);
    if (params.appCategoryUlid) qs.set("appCategoryUlid", params.appCategoryUlid);
    if (params.tagUlid) qs.set("tagUlid", params.tagUlid);
    if (params.minRating) qs.set("minRating", String(params.minRating));
    return api.get<StoreListItem[]>(`/guest/stores?${qs.toString()}`);
  },

  getById: (ulid: string, geo: { latitude: number; longitude: number; company_ulid: string }) => {
    const qs = new URLSearchParams({
      latitude: String(geo.latitude),
      longitude: String(geo.longitude),
      company_ulid: geo.company_ulid,
    });
    return api.get<StoreDetail>(`/guest/stores/${ulid}?${qs.toString()}`);
  },

  getMenu: (params: GuestMenuParams) =>
    api.get<MenuResponse>(`/guest/stores/menu/multi-level${menuQs(params)}`),

  getConfig: (ulid: string, company_ulid: string) => {
    const qs = new URLSearchParams({ company_ulid });
    return api.get<StoreConfig>(`/guest/stores/${ulid}/config?${qs.toString()}`);
  },

  getReviews: (ulid: string, params: GuestReviewParams) =>
    api.get<ReviewsResponse>(`/guest/stores/${ulid}/reviews${reviewQs(params)}`),
};
